import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { IoIosStar } from "react-icons/io";
import { HiLocationMarker } from "react-icons/hi";
import ServiceCard from "../../components/ServiceCard";
import { FaMapMarkerAlt } from "react-icons/fa";
import { BsGeoAlt, BsPatchCheckFill } from "react-icons/bs";
import { BsShop } from "react-icons/bs";
import { BsFillPencilFill } from "react-icons/bs";
import { FaRegClock, FaArrowLeft } from "react-icons/fa";
import { IoMdCut } from "react-icons/io";
import OverviewContent from "./OverviewContent";
import LocationSection from "./LocationSection";
import ReviewsSection from "./ReviewsSection";
import SimilarVendorsSection from "./SimilarVendorSection";
import ImageGalleryModal from "../../components/ImageGalleryModal";
import { FcGoogle } from "react-icons/fc";
import {
  getConsolidatedAddress,
  getFullAddress,
} from "../../../utils/AddressUtils";
import config from "../../../config";

const AddressLocalityDisplay = ({ vendor }) => {
  const [isHovering, setIsHovering] = useState(false);

  // Memoize the addresses so they don't recalculate on every hover
  const shortAddress = useMemo(() => getConsolidatedAddress(vendor), [vendor]);
  const fullAddress = useMemo(() => getFullAddress(vendor), [vendor]);

  // --- NEW: Scroll Handler Function ---
  const handleAddressClick = (e) => {
    // Prevent the default browser action (if it were a <a> tag) and prevent hover popup logic from interfering
    e.stopPropagation();

    const element = document.getElementById("location");
    if (element) {
      // Adjust offset to account for the sticky navigation header
      const offset = 100; // Match the offset used in your handleTabClick
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };
  // --- END NEW FUNCTION ---

  return (
    <div
      onClick={handleAddressClick}
      className="relative flex items-center gap-2 mt-1 mx-[-1px] self-start cursor-pointer group w-fit"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <BsGeoAlt className="w-6 h-6 text-red-600" />

      {/* Short Address / Locality */}
      <p className="text-gray-900 text-lg font-medium underline-offset-4">
        {shortAddress}
      </p>

      {/* HOVER POPUP (Tooltip) */}
      {isHovering && fullAddress && (
        <div
          className="absolute  top-0 left-full ml-3 
                              p-3 rounded-lg shadow-lg bg-gray-800 text-white 
                              text-sm font-normal whitespace-pre-wrap w-64 max-w-xs"
        >
          {fullAddress}
        </div>
      )}
    </div>
  );
};

// Helper to render star icons using IoIosStar (Matching user's icons)
const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating - fullStars >= 0.5;
  const stars = Array.from({ length: 5 }, (_, i) => {
    let starClass = "bg-gray-400 rounded-full";
    if (i < fullStars) {
      starClass = "bg-red-600 rounded-full";
    } else if (i === fullStars && hasHalfStar) {
      starClass = "bg-red-600/50 rounded-full";
    }

    return (
      <IoIosStar
        key={i}
        className={`w-3 h-3 sm:w-4 sm:h-4 text-white ${starClass}`}
      />
    );
  });

  return (
    <div className="flex items-center space-x-0 sm:space-x-0.5">{stars}</div>
  );
};

// Helper function to find the next status change
const findNextStatusChange = (
  businessHours,
  now,
  currentDay,
  currentMinutes
) => {
  const daysOrder = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const todayIndex = daysOrder.indexOf(currentDay.toLowerCase());

  for (let i = 0; i < 8; i++) {
    // Check today (i=0) and the next 7 days
    const nextDayIndex = (todayIndex + i) % 7;
    const nextDayName = daysOrder[nextDayIndex];
    const nextDayHours = businessHours.find(
      (day) => day.day.toLowerCase() === nextDayName
    );

    if (
      !nextDayHours ||
      nextDayHours.closed ||
      nextDayHours.slots.length === 0
    ) {
      if (i === 7) return { nextStatusChangeTime: null, nextOpenDay: null }; // Checked a full week
      continue;
    }

    // Check slots for this day
    for (const slot of nextDayHours.slots) {
      const [startHour] = slot.start.split(":").map(Number);
      const startMinutes = startHour * 60 + parseInt(slot.start.split(":")[1]);

      // If checking today (i=0), only consider future slots
      if (i === 0 && startMinutes > currentMinutes) {
        return { nextStatusChangeTime: slot.start, nextOpenDay: "Today" };
      }
      // If checking a future day (i>0), the first slot is the next open time
      else if (i > 0) {
        const dayLabel = i === 1 ? "Tomorrow" : nextDayHours.day;
        return { nextStatusChangeTime: slot.start, nextOpenDay: dayLabel };
      }
    }

    // If checking today (i=0) and no future open slots found, find the closing time of the *current* slot
    if (i === 0) {
      for (const slot of nextDayHours.slots) {
        const [startHour] = slot.start.split(":").map(Number);
        const [endHour] = slot.end.split(":").map(Number);
        const startMinutes =
          startHour * 60 + parseInt(slot.start.split(":")[1]);
        const endMinutes = endHour * 60 + parseInt(slot.end.split(":")[1]);

        if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
          return { nextStatusChangeTime: slot.end, nextOpenDay: null }; // Will close today at this time
        }
      }
    }
  }

  return { nextStatusChangeTime: null, nextOpenDay: null };
};

const CardInfo = () => {
  const { token, nameCompany } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAllReviews, setShowAllReviews] = useState(false);

  const [vendor, setVendor] = useState(null);

  // --- NEW STATES FOR SIMILAR VENDORS ---
  const [similarVendors, setSimilarVendors] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  // 1. New State for Tab Management
  const [activeTab, setActiveTab] = useState("overview");

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const sectionRefs = useRef({});

  // Image Gallery Modal State
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [isScrolledDown, setIsScrolledDown] = useState(false); // New State

  // Memoize the short address for the sticky header
  const shortAddress = useMemo(() => getConsolidatedAddress(vendor), [vendor]); // New Memoization

  const fullText = `Contrary to popular belief, Lorem Ipsum is not simply random text. It has roots in a piece of classical Latin literature from 45 BC, making it over 2000 years old. Richard McClintock, a Latin professor at Hampden-Sydney College in Virginia. It has roots in a piece of classical Latin literature from 45 BC, making it over 2000 years old. Richard McClintock, a Latin professor at Hampden-Sydney College in Virginia. Contrary to popular belief, Lorem Ipsum is not simply random text. It has roots in a piece of classical Latin literature from 45 BC, making it over 2000 years old. Richard McClintock, a Latin professor at Hampden-Sydney College in Virginia.`;
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Scroll detection for sticky header
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setIsScrolledDown(true);
      } else {
        setIsScrolledDown(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Split into words for the 70-word preview
  const words = fullText.split(" ");
  const previewText = words.slice(0, 70).join(" ");
  const mondayHours = vendor?.businessHours?.find(
    (day) => day.day.toLowerCase() === "monday"
  );

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hour, minute] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hour, minute);
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

 

  const fetchVendorData = useCallback(async () => {
    setVendor(null);
    setLoading(true);
    const authToken = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/vendors/getTokenProfile?token=${token}`
      );
      const data = await response.json();
      const fetchedVendor = data?.Data || data?.data || {};
      if (response.ok && fetchedVendor && fetchedVendor.vendor_id) {
        let vendorData = fetchedVendor;
        
         // ONLY use getRating endpoint (no auth required)
      try {
        const ratingResponse = await fetch(
          `${config.API_BASE_URL}/customer/getRating/${fetchedVendor.vendor_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (ratingResponse.ok) {
          const ratingData = await ratingResponse.json();
          // API returns: { data: [{ reviews_count: 2, average_rating: 5 }] }
          const ratingInfo = ratingData?.data?.[0] || {};
          const reviewsCount = ratingInfo.reviews_count || 0;
          const averageRating = ratingInfo.average_rating || 0;

          vendorData.start_rating = averageRating.toFixed(1);
          vendorData.total_review = String(reviewsCount);
        } else {
          vendorData.start_rating = "0.0";
          vendorData.total_review = "0";
        }
      } catch (ratingErr) {
        console.warn("Could not fetch rating:", ratingErr.message);
        vendorData.start_rating = "0.0";
        vendorData.total_review = "0";
      }
      

        // FLIPS REVIEWS FETCH STARTS HERE

        try {
          const reviewsResponse = await fetch(
            `${config.API_BASE_URL}/customer/getReview/${fetchedVendor.vendor_id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
              },
              // We must send a body with a POST request, even if the backend ignores it.
              // body: JSON.stringify({ vendor_id: fetchedVendor.vendor_id })
            }
          );

          if (reviewsResponse.status === 401) {
            console.warn(
              "Review fetch failed with 401 Unauthorized. Clearing local token and proceeding without reviews."
            );
            localStorage.removeItem("token");
          } else if (!reviewsResponse.ok) {
            const errorText = await reviewsResponse.text();
            console.error(
              `❌ Review API failed with status: ${reviewsResponse.status}. Response body: ${errorText}`
            );
            throw new Error(
              `Review fetch non-OK status: ${reviewsResponse.status}`
            );
          }
          const reviewsData =
            reviewsResponse.status === 200
              ? await reviewsResponse.json()
              : { data: [] };
          const reviewsList = reviewsData?.data || [];

          // 3. Process and Calculate Stats from the new reviewsList
          let totalRating = 0;
          if (reviewsList.length > 0) {
            totalRating = reviewsList.reduce(
              (sum, review) => sum + Number(review.rating || 0),
              0
            );
          }
          const averageRating =
            reviewsList.length > 0 ? totalRating / reviewsList.length : 0;

          // 4. Update the vendor object with real review data
          vendorData.start_rating = averageRating.toFixed(1);
          vendorData.total_review = String(reviewsList.length);
          vendorData.reviews_data = reviewsList.map((review) => ({
            ...review,
            user: review.reviewer_name,
            rating: Number(review.rating),
            comment: review.comment,
            date: review.createdAt,
            _id: review._id, // Ensure a unique key
          }));
        } catch (reviewErr) {
          console.warn(
            "Could not fetch or process vendor reviews from local API:",
            reviewErr.message
          );
          // Set default empty state if the review fetch fails
          vendorData.start_rating = vendorData.start_rating || "0.0";
          vendorData.total_review = vendorData.total_review || "0";
          vendorData.reviews_data = [];
        }
        setVendor(vendorData);
      } else {
        setVendor(null);
        throw new Error(data.message || "Failed to fetch vendor info");
      }
    } catch (err) {
      console.error("Error fetching vendor:", err.message);
      setVendor(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (token) {
      fetchVendorData();
    }
  }, [token, fetchVendorData]);

  const handleReviewSuccess = useCallback(() => {
    console.log("Review submitted successfully. Re-fetching vendor data...");
    // This function triggers the main data fetch, which includes the reviews
    fetchVendorData();
    setIsReviewModalOpen(true);
  }, [fetchVendorData]);

  // html remover
  const stripPTags = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]+>/g, "");
  };

  const tabs = useMemo(
    () => [
      {
        id: "overview",
        label: "Overview",
        icon: <FaRegClock className="w-5 h-5" />,
      },
      {
        id: "services",
        label: "Services",
        icon: <IoMdCut className="w-5 h-5" />,
      },
      {
        id: "reviews",
        label: "Reviews",
        icon: <IoIosStar className="w-5 h-5" />,
      },
      {
        id: "location",
        label: "Location",
        icon: <FaMapMarkerAlt className="w-5 h-5" />,
      },
    ],
    []
  );
  const handleWriteReviewClick = () => {
    setIsReviewModalOpen(true);
  };

  useEffect(() => {
    // 1. Scroll to top AND set initial active tab
    window.scrollTo(0, 0);
    if (tabs && tabs.length > 0) {
      // Guarantee the first tab is active on load
      setActiveTab(tabs[0].id);
    }

    // 2. Fetch data (existing logic)
    if (token) {
      fetchVendorData();
    }

    // Removed window.scrollTo(0, 0) from the main Intersection Observer useEffect
  }, [token, fetchVendorData, tabs]);

  const [isScrollingFromClick, setIsScrollingFromClick] = useState(false);

  // --- CRITICAL SCROLL LOGIC FIX: Intersection Observer (FINAL) ---
  useEffect(() => {
    const STICKY_NAV_HEIGHT = 52;
    const rootMargin = `-${STICKY_NAV_HEIGHT}px 0px -50% 0px`;

    const offset = 52;

    const handleScroll = () => {
      // 2. Loop through your sections
      tabs.forEach((tab) => {
        const section = document.getElementById(tab.id);

        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;

          // 3. Check if the scroll position has passed the top of the section
          // and is before the end of the section
          if (
            window.scrollY >= sectionTop - offset &&
            window.scrollY < sectionTop + sectionHeight - offset
          ) {
            // 4. If it is, call setActiveTab(sectionId)
            setActiveTab(tab.id);
          }
        }
      });
    };

    // Attach the scroll event listener to the window
    window.addEventListener("scroll", handleScroll);

    // Cleanup function: This removes the event listener when the component unmounts
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tabs]);
  // --- ANCHOR LINK HANDLER ---
  const handleTabClick = (id) => {
    setActiveTab(id);
    
    const element = document.getElementById(id);

    if (element) {
      setIsScrollingFromClick(true);
      const offset = id === "overview" ? 150 : 52;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      setTimeout(() => {
        setIsScrollingFromClick(false);
        const currentScrollPosition = window.scrollY;
        const targetPosition = document.getElementById(id).offsetTop;
        if (Math.abs(currentScrollPosition - targetPosition) < 200) {
          setActiveTab(id);
        }
      }, 500);
    }
  };
  // 2. BUSINESS STATUS LOGIC: Memoized to recalculate only when vendor data or time changes
  const getBusinessStatus = useMemo(() => {
    if (!vendor?.businessHours) {
      return {
        status: "N/A",
        isOpen: false,
        currentDayHours: null,
        currentDay: "N/A",
        nextStatusChangeTime: null,
      };
    }
    const now = currentTime;
    const currentDay = now.toLocaleString("en-us", { weekday: "long" });
    const lowerCaseCurrentDay = currentDay.toLowerCase();
    // Find today's hours
    const todayHours = vendor.businessHours.find(
      (day) => day.day.toLowerCase() === lowerCaseCurrentDay
    );
    // Convert current time to minutes since midnight
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    let isOpen = false;
    let nextStatusChangeTime = null;

    if (todayHours && !todayHours.closed) {
      // Check if current time falls within any open slot TODAY
      for (const slot of todayHours.slots) {
        const [startHour] = slot.start.split(":").map(Number);
        const [endHour] = slot.end.split(":").map(Number);

        const startMinutes =
          startHour * 60 + parseInt(slot.start.split(":")[1]);
        const endMinutes = endHour * 60 + parseInt(slot.end.split(":")[1]);

        // Check if open now
        if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
          isOpen = true;
          nextStatusChangeTime = slot.end; // Will close at the end of this slot
          break;
        }
      }
    }

    // Determine status and next change time
    let status = isOpen ? "OPEN" : "CLOSED";
    let nextOpenDay = null;

    if (!isOpen) {
      // If closed, find the *next* opening time (either today or a future day)
      const nextChange = findNextStatusChange(
        vendor.businessHours,
        now,
        currentDay,
        currentMinutes
      );
      nextStatusChangeTime = nextChange.nextStatusChangeTime;
      nextOpenDay = nextChange.nextOpenDay;
    } else if (isOpen && nextStatusChangeTime) {
      // Check if closing soon (e.g., within 30 minutes)
      const [closeHour] = nextStatusChangeTime.split(":").map(Number);
      const closeMinutes =
        closeHour * 60 + parseInt(nextStatusChangeTime.split(":")[1]);
      if (closeMinutes - currentMinutes <= 30) {
        status = "CLOSING SOON";
      }
    }

    return {
      status: status,
      isOpen: isOpen,
      currentDayHours: todayHours,
      currentDay: currentDay,
      nextStatusChangeTime: nextStatusChangeTime,
      nextOpenDay: nextOpenDay,
    };
  }, [vendor, currentTime]);

  const {
    status,
    isOpen,
    currentDayHours,
    currentDay,
    nextStatusChangeTime,
    nextOpenDay,
  } = getBusinessStatus;

  // Function to create the status message for the UI
  const getNextStatusMessage = () => {
    if (status === "OPEN") {
      return `Untill ${formatTime(nextStatusChangeTime)}`;
    }
    if (status === "CLOSING SOON") {
      return `Closes soon at ${formatTime(nextStatusChangeTime)}`;
    }
    if (status === "CLOSED" && nextStatusChangeTime) {
      if (nextOpenDay === "Today") {
        return `Opens at ${formatTime(nextStatusChangeTime)}`;
      } else if (nextOpenDay === "Tomorrow") {
        return `Opens Tomorrow at ${formatTime(nextStatusChangeTime)}`;
      } else if (nextOpenDay) {
        return `Opens ${nextOpenDay} at ${formatTime(nextStatusChangeTime)}`;
      }
    }
    return "Hours unavailable.";
  };

  // UI rendering variables based on status
  const statusColor =
    isOpen || status === "CLOSING SOON" ? "text-green-600" : "text-red-600";
  const statusBgPulse =
    isOpen || status === "CLOSING SOON" ? "bg-green-600" : "bg-red-600";
  const displayStatusText = status.toUpperCase();
  const nextStatusMessage = getNextStatusMessage();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6216]"></div>
          <p className="mt-4 text-gray-600">Loading vendor card info...</p>
        </div>
      </div>
    );
  }

  // Generate today's business hours string
  const todayHoursString =
    currentDayHours && !currentDayHours.closed
      ? currentDayHours.slots
          .map((slot) => `${formatTime(slot.start)} - ${formatTime(slot.end)}`)
          .join(", ")
      : `Closed today. ${nextStatusMessage}`;

  const vendorRating = parseFloat(vendor?.start_rating || 0);
  const reviewCount = parseInt(vendor?.total_review || 0);
  const filledStars = Math.floor(vendorRating);

  const images = Array.isArray(vendor?.photo?.shop) ? vendor.photo.shop : [];

  const CoverImg = vendor?.photo?.coverPhoto
    ? Array.isArray(vendor.photo.coverPhoto)
      ? vendor.photo.coverPhoto
      : [vendor.photo.coverPhoto] // FIX
    : [];

  const shopAreaImg = Array.isArray(vendor?.photo?.serviceArea)
    ? vendor.photo.serviceArea
    : [];

  const shopVideos = vendor?.video?.shopVideo
    ? Array.isArray(vendor.video.shopVideo)
      ? vendor.video.shopVideo
      : [vendor.video.shopVideo] // FIX
    : [];

  const serviceAreaVideos = vendor?.video?.serviceAreaVideo
    ? Array.isArray(vendor.video.serviceAreaVideo)
      ? vendor.video.serviceAreaVideo
      : [vendor.video.serviceAreaVideo] // FIX
    : [];

  const allVideos = [...shopVideos, ...serviceAreaVideos];

  const imgCount = images.length;

  // Address sirf <= 3 image par
  const showAddress = imgCount <= 3;

  // Scrollable tab hogi jab image > 5
  const scrollEnabled = imgCount > 5;

  // Display images: sab images show hongi (limit remove)
  const displayImages = images;

  // Combine all images and videos for the modal slider
  const allImages = [...images, ...CoverImg, ...shopAreaImg];
  const allMediaItems = [...allImages, ...allVideos];

  // Handler to open image/video modal
  const handleMediaClick = (index) => {
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };

  return (
    <div className="relative  px-4 sm:px-6 lg:px-14 py-4 mt-3 sm:py-6 bg-[#FFFCFC]">     
      <div className="mb-4 -mt-2 sm:mb-4">
        <button
          onClick={() => navigate(-1)}
          className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white shadow-sm hover:shadow-md text-sm sm:text-base font-medium text-blue-700 hover:text-[#FF6216] transition-all duration-200"
        >
          <FaArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
          <span>Back to Vendors</span>
        </button>
      </div>
      {isScrolledDown && (
        <div className="fixed top-0 left-0 right-0 z-20 bg-white shadow-md py-4 px-4 sm:px-6 lg:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              
              <img
                src={
                  vendor?.photo?.profilePhoto
                    ? `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${vendor.photo.profilePhoto}`
                    : "/img/dogLogo.png"
                }
                alt="Vendor Profile"
                className="w-9 h-9 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold text-md text-[#000048] truncate max-w-[150px] sm:max-w-full">
                  {vendor?.profile?.brand || vendor?.profile?.companyName || ""}
                </p>
                <p className="text-md text-gray-600 truncate max-w-[150px] sm:max-w-full">
                  {shortAddress}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div
                className={`w-2.5 h-2.5 ${statusBgPulse} rounded-full animate-pulse`}
              ></div>
              <span className={`font-semibold ${statusColor}`}>
                {displayStatusText}
              </span>
              {/* <span className="text-gray-500">{nextStatusMessage}</span> */}
            </div>
          </div>
        </div>
      )}
      {/* Header - FIX: Force flex-row on ALL SCREENS and align items-start */}
      <div className="flex justify-between items-start gap-2 sm:gap-6">
        {/* Main Header Text Block - Allows content to wrap vertically if space is tight */}
        <div className="flex-1 md:max-w-[70%] lg:max-w-none">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[#000048] text-2xl sm:text-2xl lg:text-3xl font-semibold">
              {vendor?.profile?.brand || vendor?.profile?.companyName || ""}
            </p>

            <div className="flex items-center gap-2  mx-2 text-[#000048] text-sm sm:text-base lg:text-lg">
              <div className="relative flex items-center justify-center">
                <BsPatchCheckFill className="w-4  sm:w-5 lg:w-6 h-4 sm:h-5 lg:h-6 text-white bg-blue-600 rounded-full p-1 animate-facebook-verify" />
                <div className="absolute inset-0 bg-blue-600 rounded-full opacity-25 animate-facebook-glow" />
              </div>
              {/* <p className="font-medium">Verified</p> */}
            </div>
          </div>

          <p className="text-[#000048] text-sm sm:text-base lg:text-lg font-medium mt-1">
            {vendor?.full_address}
          </p>

          {/* --- 1. DYNAMIC RATING & TIMING SECTION (UPDATED) --- */}
          <div className="flex flex-row gap-2">
            {/* --- NEW WRAPPER FOR RATING + BUTTON (Horizontal Alignment) --- */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-5">
              {/* === RATING BLOCK === */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {vendor?.google_rating > 0 &&
                vendor?.google_review_count > 0 ? (
                  /* Google Rating – Priority 1 */
                  <div className="flex items-center gap-2.5 sm:gap-3.5 flex-wrap">
                    <FcGoogle className="w-5 h-5 text-blue-600 flex-shrink-0" />

                    <div className="flex items-center gap-2 text-sm sm:text-base">
                      <span className="font-bold text-blue-700">
                        {(vendor.google_rating || 0).toFixed(1)}
                      </span>

                      {/* Mobile: Big single star */}
                      <IoIosStar className="w-7 h-7 sm:hidden text-yellow-500" />

                      {/* Desktop: Full 5-star rating */}
                      <div className="hidden sm:flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => {
                          const rating = vendor.google_rating || 0;
                          const fill =
                            i < Math.floor(rating)
                              ? "text-yellow-500"
                              : i < rating
                                ? "text-yellow-400"
                                : "text-gray-300";
                          return (
                            <IoIosStar key={i} className={`w-4 h-4 ${fill}`} />
                          );
                        })}
                      </div>

                      <span className="text-gray-500 text-xs sm:text-sm">
                        ({vendor.google_review_count.toLocaleString()} reviews)
                      </span>
                    </div>
                  </div>
                ) : vendorRating > 0 || reviewCount > 0 ? (
                  /* Flips Rating – Priority 2 */
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    {/* Mobile: One big star */}
                    <IoIosStar className="w-7 h-7 sm:hidden text-yellow-500" />

                    {/* Desktop: Full stars */}
                    <div className="hidden sm:flex items-center gap-0.5">
                      {renderStars(vendorRating, "w-4 h-4")}
                    </div>

                    <span className="font-bold text-gray-800 text-base sm:text-lg">
                      {vendorRating.toFixed(1)}
                    </span>

                    <a
                      href="#reviews"
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabClick("reviews");
                      }}
                      className="text-sm sm:text-base text-gray-600 hover:text-gray-900 hover:underline transition"
                    >
                      ({reviewCount} reviews)
                    </a>
                  </div>
                ) : (
                  /* New on Flips – Priority 3 */
                  <div className="flex items-center gap-2 text-sm sm:text-base font-medium text-gray-700">
                    <span className="text-xl">New</span>
                    <span className="hidden sm:inline">on Flips</span>
                  </div>
                )}
              </div>

              {/* === REVIEW US BUTTON (Always visible, aligned nicely) === */}
              <a
                href="#reviews"
                onClick={(e) => {
                  e.preventDefault();
                  handleTabClick("reviews");
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 
               bg-gradient-to-r from-blue-50 to-blue-100 
               hover:from-blue-100 hover:to-blue-200 
               text-blue-700 font-medium text-sm sm:text-base
               rounded-full border border-blue-200 
               shadow-sm hover:shadow transition-all duration-200
               whitespace-nowrap"
              >
                <BsFillPencilFill className="w-4 h-4 text-blue-600" />
                <span className="hidden xs:inline">Review Us</span>
              </a>
            </div>
            {/* --- END NEW WRAPPER --- */}
          </div>
          {/* END DYNAMIC RATING & TIMING SECTION */}

          {/* --- NEW ADDRESS / LOCALITY SECTION --- */}
          <AddressLocalityDisplay vendor={vendor} />

          <div className="flex items-center gap-2 mt-2 px-1 mb-2 whitespace-nowrap overflow-hidden sm:hidden">
            {/* Status Dot */}
            <div
              className={`w-2.5 h-2.5 ${statusBgPulse} rounded-full animate-pulse`}
            ></div>

            <span className={`font-semibold text-base ${statusColor}`}>
              {displayStatusText}
            </span>

            {/* Time Message for OPEN/CLOSING SOON */}
            {(status === "OPEN" || status === "CLOSING SOON") && (
              <p className="text-gray-500 font-normal text-base">
                {getNextStatusMessage()}
              </p>
            )}

            {/* Time Message for CLOSED */}
            {status === "CLOSED" && (
              <p className="text-gray-500 font-normal text-base">
                {nextStatusMessage}
              </p>
            )}
          </div>
          {/* --- END NEW MOBILE-ONLY STATUS --- */}
        </div>

        {/* Status Card */}
        {/* 4. DYNAMIC STATUS CARD - FIX: Add w-[150px] and flex-shrink-0 for mobile constraint */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-0 sm:p-2 border border-white/20 text-center w-[170px] flex-shrink-0 sm:w-auto sm:min-w-[200px] lg:min-w-[250px] hidden lg:block">
          {" "}
          <div className="flex flex-row items-center justify-center gap-6 sm:gap-8 py-4">
            {/* Shop Type */}
            <div className="flex items-center gap-2.5 sm:gap-3">
              <BsShop className="w-7 h-7 text-orange-400 flex-shrink-0" />
              <span className="font-bold text-[#000048] text-base sm:text-lg">
                {vendor?.subscription_type?.toUpperCase() === "HOME"
                  ? "Home Service"
                  : vendor?.subscription_type?.toUpperCase() === "SHOP"
                    ? "In-Store"
                    : "Shop"}
              </span>
            </div>

            {/* Vertical Divider */}
            <div className="h-10 w-px bg-gray-300 hidden sm:block"></div>

            {/* Status + Message */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Status Dot + Text */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 ${statusBgPulse} rounded-full animate-pulse`}
                />
                <span
                  className={`font-semibold text-base sm:text-lg ${statusColor}`}
                >
                  {displayStatusText}
                </span>
              </div>

              {/* Next Status Message */}
              {(status === "OPEN" ||
                status === "CLOSING SOON" ||
                status === "CLOSED") && (
                <span className="text-gray-600 text-sm sm:text-base font-medium">
                  {status === "CLOSED"
                    ? nextStatusMessage
                    : getNextStatusMessage()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* END Header (Status Card is now horizontal with main header) */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 sm:gap-6 relative mt-3">
        <div
          className={`w-full lg:flex-grow ${
            scrollEnabled ? "overflow-x-auto" : "overflow-x-hidden"
          }`}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {/* ---------- PHOTOS ---------- */}
            {[...images, ...CoverImg, ...shopAreaImg].length > 0 ? (
              [...images, ...CoverImg, ...shopAreaImg].map((img, idx) => (
                <div
                  key={`img-${idx}`}
                  className="relative flex-shrink-0 cursor-pointer"
                  onClick={() => handleMediaClick(idx)}
                >
                  <img
                    src={`${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${img}`}
                    alt={`Image ${idx + 1}`}
                    className="h-48 sm:h-56 lg:h-64 w-52 sm:w-64 lg:w-72 object-cover rounded-lg hover:opacity-90 transition-opacity"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/img/building.png";
                    }}
                  />

                  {/* Profile image on first item */}
                  {idx === 0 && (
                    <div className="absolute top-10 sm:top-12 lg:top-14 left-3 sm:left-4">
                      <img
                        src={
                          vendor?.photo?.profilePhoto
                            ? `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${vendor.photo.profilePhoto}`
                            : "/img/dogLogo.png"
                        }
                        alt="Vendor Profile"
                        className="w-20 sm:w-24 lg:w-28 h-20 sm:h-24 lg:h-28 rounded-full border-4 border-white shadow-md object-cover bg-white"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/img/dogLogo.png";
                        }}
                      />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-sm sm:text-base"></p>
            )}

            {/* ---------- VIDEOS ---------- */}
            {allVideos.length > 0 ? (
              allVideos.map((vid, idx) => (
                <div
                  key={`video-${idx}`}
                  className="flex-shrink-0 cursor-pointer"
                  onClick={() => handleMediaClick(allImages.length + idx)}
                >
                  <video
                    controls
                    className="h-48 sm:h-56 lg:h-64 w-52 sm:w-64 lg:w-72 object-cover rounded-lg bg-black hover:opacity-90 transition-opacity"
                  >
                    <source
                      src={`${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${vid}`}
                      type="video/mp4"
                    />
                  </video>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-sm sm:text-base"></p>
            )}
          </div>
        </div>

        {/* ------------------ ADDRESS SECTION ------------------ */}
        {showAddress && (
          <div className="hidden lg:block w-full lg:w-1/3 mt-4 lg:mt-0">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-blue-500 p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 text-white">
                  <FaMapMarkerAlt className="w-5 sm:w-6 h-5 sm:h-6" />
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold">
                    Location
                  </h3>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <p className="text-gray-700 font-medium text-sm sm:text-base">
                    {vendor?.address?.officeAddress
                      ? [
                          vendor.address.officeAddress.full_address,
                          vendor.address.officeAddress.floor,
                          vendor.address.officeAddress.landmark,
                          vendor.address.officeAddress.locality ||
                            vendor.address.homeAddress.locality,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "Akshya Nagar 1st Block 1st Cross, Rammurthy Nagar,"}
                  </p>

                  <p className="text-gray-600 text-sm sm:text-base">
                    {vendor?.address?.officeAddress
                      ? [
                          vendor.address.officeAddress.city,
                          vendor.address.officeAddress.state,
                          vendor.address.officeAddress.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "Bangalore - 560016"}
                  </p>
                </div>

                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                    vendor?.address?.officeAddress?.full_address ||
                      "Akshya Nagar 1st Block 1st Cross, Rammurthy Nagar, Bangalore",
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-blue-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
                >
                  <HiLocationMarker className="w-4 sm:w-5 h-4 sm:h-5" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 1. STICKY NAV TABS CONTAINER (Modern Pill Style) */}
      <div className="sticky top-20 md:top-24 z-[5] py-2 px-4 sm:px-0">
        <div className="bg-gray-200/70 backdrop-blur-md rounded-full p-1 border border-gray-300/50 max-w-2xl mx-auto">
          <div className="flex justify-between items-center gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`
                            w-full flex-1 justify-center text-center inline-flex items-center gap-2 py-2 px-2 sm:px-3 text-lg sm:text-md font-bold rounded-full transition-all duration-300
                            ${
                              activeTab === tab.id
                                ? "bg-white text-[#FF6216] shadow-md"
                                : "text-gray-600 hover:bg-white/50"
                            }
                        `}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 4. ALL CONTENT SECTIONS (ALWAYS RENDERED) */}
      <div className="">
        {/* OVERVIEW SECTION */}
        <section
          id="overview"
          className="py-6 border-b border-gray-200 scroll-mt-[90px]"
        >
          {vendor?.profile?.businessProfile &&
          stripPTags(vendor.profile.businessProfile).trim() ? (
            <OverviewContent
              vendor={vendor}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
            />
          ) : null}
        </section>
        {/* SERVICES SECTION */}
        <section
          id="services"
          className="py-1 sm:py-6 border-b border-gray-200 lg:mb-3 scroll-mt-[90px]"
        >
          <ServiceCard services={vendor?.services} vendor={vendor} />
        </section>

        {/* REVIEWS SECTION */}
        <section
          id="reviews"
          className="sm:py-7 border-b border-gray-200 scroll-mt-[90px]"
        >
         {vendor ? (
          <ReviewsSection
            vendor={vendor}
            onReviewSuccess={handleReviewSuccess}
            onWriteReviewClick={handleWriteReviewClick}
          />
          ) : (
          <div className="text-center py-12 text-gray-500">
            Loading reviews...
          </div>
        )}
        </section>

        {/* LOCATION SECTION */}
        <section
          id="location"
          className="py-7 border-b border-gray-200 scroll-mt-[90px]"
        >
          <LocationSection vendor={vendor} />
        </section>

        {/* --- SIMILAR VENDORS SECTION (API driven) --- */}
        <SimilarVendorsSection mainVendorData={vendor} />
      </div>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        images={allMediaItems}
        initialIndex={selectedImageIndex}
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
      />
    </div>
  );
};

export default CardInfo;
